import { useEffect } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';

import EventService from 'src/services/EventService';
import { getStreamQuality } from 'src/services/LeadStreamsService/quality';
import useUserStore from 'src/stores/user';

import { leadCountLabel } from './StreamActivityCalendar';

import type { Stream } from 'src/services/LeadStreamsService/agent';

export default function StreamQualitySummary({ streamId }: { streamId: number }) {
    const userId = useUserStore((state) => state.id);
    const { data, isLoading, isFetching, isError, refetch } = useQuery(
        ['stream-quality', userId, streamId],
        () => getStreamQuality(streamId),
        { staleTime: 0, cacheTime: 0, retry: false, keepPreviousData: false }
    );
    useEffect(() => {
        const subscription = EventService.subscribe<Stream>('LEAD_STREAMS_UPDATED', (_type, updated) => {
            if (!updated || updated.id === streamId) void refetch();
        });
        return () => subscription.unsubscribe();
    }, [streamId, refetch]);

    return (
        <Box
            component="section"
            aria-label="Оценки лидов"
            aria-busy={isFetching}
            sx={{ mt: 3, pt: 2, borderTop: '1px solid var(--lm-border)' }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={1.5}
            >
                <Typography
                    component="h3"
                    variant="body1"
                    fontWeight={600}
                >
                    Ваши оценки{' '}
                    <Box
                        component="span"
                        sx={{ color: 'var(--lm-muted)', fontWeight: 400 }}
                    >
                        · за всё время
                    </Box>
                </Typography>
                <IconButton
                    aria-label="Обновить оценки"
                    title="Обновить оценки"
                    disabled={isFetching}
                    onClick={() => void refetch()}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                >
                    {isFetching ? <CircularProgress size={18} /> : <IconRefresh size={18} />}
                </IconButton>
            </Stack>
            {isLoading ? (
                <Typography
                    variant="body2"
                    color="secondary.main"
                    role="status"
                >
                    Загружаем оценки…
                </Typography>
            ) : isError || !data ? (
                <Box role="alert">
                    <Typography variant="body2">Не удалось загрузить оценки.</Typography>
                    <Button
                        size="small"
                        disabled={isFetching}
                        onClick={() => void refetch()}
                    >
                        Повторить
                    </Button>
                </Box>
            ) : data.rated === 0 ? (
                <Stack
                    alignItems="flex-start"
                    spacing={0.5}
                >
                    <Typography variant="body2">Пока нет оценок.</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                    >
                        Отметьте в ленте, какие лиды вам подходят.
                    </Typography>
                    <Button
                        component={Link}
                        to={`/feed?stream=${streamId}`}
                        size="small"
                    >
                        Оценить лиды
                    </Button>
                </Stack>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: '1.2fr 1fr 1fr' },
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                            <Typography
                                component="p"
                                sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.2 }}
                            >
                                {data.percentage}{' '}
                                <Box
                                    component="span"
                                    sx={{ fontSize: 14, fontWeight: 400 }}
                                >
                                    подходят
                                </Box>
                            </Typography>
                            <Typography
                                variant="caption"
                                color="secondary.main"
                            >
                                Оценено: {leadCountLabel(data.rated)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                variant="body2"
                                color="secondary.main"
                            >
                                Подходят
                            </Typography>
                            <Typography
                                component="p"
                                fontWeight={600}
                            >
                                {data.good.toLocaleString('ru-RU')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                variant="body2"
                                color="secondary.main"
                            >
                                Не подходят
                            </Typography>
                            <Typography
                                component="p"
                                fontWeight={600}
                            >
                                {data.bad.toLocaleString('ru-RU')}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        variant="caption"
                        color="secondary.main"
                        component="p"
                        mt={1.5}
                    >
                        Неоценённые лиды в расчёт не входят.
                    </Typography>
                </>
            )}
        </Box>
    );
}
